import React, { useState } from "react";

import { filter, times } from "lodash/fp";

import Typography from "@mui/material/Typography";

import SchedulingLinkDetail, {
  LoadingSchedulingLinkDetail,
} from "@app/analytics/Calendar/components/SchedulingLinkDetail";
import useRssLinksSettings from "@app/analytics/hooks/settings/useRssLinksSettings";
import * as Events from "@app/analytics/tracking/events";
import useLocalStorage from "@app/common/hooks/useLocalStorage";
import type {
  PatchSchedulingLinkBody,
  SchedulingLink,
} from "@app/common/types";
import { getSchedulingLinkUrl } from "@app/common/utils";
import { pluralizePhrase } from "@app/common/utils";

import Alert from "@rui/Alert";
import ConfirmationModal from "@rui/ConfirmationModal";
import DontShowThisAgain from "@rui/DontShowThisAgain";
import Stack, { Row } from "@rui/Stack";
import { Bold } from "@rui/styles";

const LoadingState = () => (
  <Stack spacing={1.5}>
    {times(
      (i) => (
        <LoadingSchedulingLinkDetail key={i} />
      ),
      4
    )}
  </Stack>
);

const DefaultLinks = ({
  isLoading,
  links,
  onToggleEnabled,
}: {
  isLoading: boolean;
  links: SchedulingLink[];
  onToggleEnabled: (update: PatchSchedulingLinkBody) => void;
}) => {
  const { rssLinkSettings } = useRssLinksSettings();
  const [linkToDisable, setLinkToDisable] = useState<SchedulingLink | null>(
    null
  );
  const [disableConfirmDismissed, setDisableConfirmDismissed] = useLocalStorage(
    "disableDefaultLinkConfirmationDismissed",
    false,
    { migrateKeys: true }
  );

  const toggleLinkEnabled = (update: PatchSchedulingLinkBody) =>
    onToggleEnabled(update);

  const handleToggleEnabled =
    (link: SchedulingLink) =>
    (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      if (checked) {
        Events.enableDefaultLink({ duration: link.allowedDuration });
        toggleLinkEnabled({ id: link.id, isEnabled: checked });
      } else if (disableConfirmDismissed) {
        Events.disableDefaultLink({ duration: link.allowedDuration });
        toggleLinkEnabled({ id: link.id, isEnabled: checked });
      } else {
        setLinkToDisable(link);
      }
    };

  const handleConfirmDisable = () => {
    if (!linkToDisable) return;
    Events.disableDefaultLink({ duration: linkToDisable.allowedDuration });
    toggleLinkEnabled({ id: linkToDisable.id, isEnabled: false });
    setLinkToDisable(null);
  };

  const numActiveLinks = filter("isEnabled", links).length;

  return (
    <>
      <Stack spacing={2}>
        <Row variant="apart" spacing={2} sx={{ minHeight: "30px" }}>
          <Typography variant="bodySecondaryReg">
            <Bold>{pluralizePhrase(links.length, "link")}</Bold> (
            {numActiveLinks} active)
          </Typography>
        </Row>
        <Stack spacing={1.5}>
          {isLoading ? (
            <LoadingState />
          ) : (
            links.map((link) => (
              <SchedulingLinkDetail
                key={link.id}
                link={link}
                linkUrl={getSchedulingLinkUrl(
                  rssLinkSettings.vanityId,
                  link.vanityId
                )}
                onToggleEnabled={handleToggleEnabled(link)}
                workingHoursEnabled={rssLinkSettings.workingHoursEnabled}
              />
            ))
          )}
        </Stack>
      </Stack>

      <ConfirmationModal
        title="Disable scheduling link?"
        open={linkToDisable != null}
        onClose={() => setLinkToDisable(null)}
        onConfirm={handleConfirmDisable}
        size="md"
      >
        <Stack spacing={1.5}>
          <Typography variant="bodyPrimaryReg" color="shades.neutral.600">
            Anyone with access to this scheduling link will no longer be able to
            schedule a meeting while it's disabled.
          </Typography>
          <Alert
            severity="info"
            embedded
            alignItems="flex-start"
            sx={{ color: "shades.neutral.600" }}
          >
            If you use this link externally (such as in an email signature or
            website), be sure to remove it.
          </Alert>
          <DontShowThisAgain
            checked={disableConfirmDismissed}
            onChange={setDisableConfirmDismissed}
          />
        </Stack>
      </ConfirmationModal>
    </>
  );
};

export default DefaultLinks;
